import { useEffect, useRef, useState } from 'react';
import type { Participant } from '../api';
import { displayNumber } from '../phone/number';
import type { Phone } from '../phone/phone';
import { CloseIcon, MicIcon, MicOffIcon } from './icons';

/** Who is in the conference call: mute or remove each person. */
export function PeopleSheet({
  phone,
  people,
  youMuted,
  canAdd,
  onAdd,
  onClose,
}: {
  phone: Phone;
  people: Participant[];
  youMuted: boolean;
  canAdd: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  const act = async (id: string, run: () => Promise<string | null>) => {
    setBusy(id);
    setError(null);
    const err = await run();
    setBusy(null);
    setConfirm(null);
    setError(err);
  };

  // the user last, like a contact list with "me" at the end
  const others = people.filter((p) => !p.you);
  const joined = people.filter((p) => p.state === 'joined').length;

  return (
    <dialog ref={ref} className="sheet" aria-labelledby="people-title" onClose={onClose} onCancel={onClose}>
      <div className="sheet-head">
        <h2 id="people-title">
          On the call <span className="people-count">({joined})</span>
        </h2>
        <button className="btn btn-quiet btn-small" type="button" onClick={() => ref.current?.close()}>
          Done
        </button>
      </div>
      <ul className="people">
        {others.map((p) => (
          <li key={p.id} className={`person${p.state === 'ringing' ? ' person-ringing' : ''}`}>
            <div className="person-text">
              <span className="person-number">{displayNumber(p.number) || 'Unknown number'}</span>
              <span className="person-status">
                {p.transfer
                  ? p.state === 'ringing'
                    ? 'Ringing… transferring the call to them'
                    : 'Joining… transferring the call to them'
                  : p.state === 'ringing'
                    ? 'Ringing…'
                    : p.muted
                      ? 'Muted'
                      : 'On the call'}
              </span>
            </div>
            {confirm === p.id ? (
              <div className="person-actions">
                <button type="button" className="btn btn-small btn-quiet" onClick={() => setConfirm(null)} disabled={busy === p.id}>
                  Keep
                </button>
                <button type="button" className="btn btn-small btn-danger" onClick={() => void act(p.id, () => phone.removePerson(p.id))} disabled={busy === p.id}>
                  {p.state === 'ringing' ? 'Stop calling' : 'Remove'}
                </button>
              </div>
            ) : (
              <div className="person-actions">
                {p.state === 'joined' && (
                  <button
                    type="button"
                    className="icon-btn"
                    aria-pressed={p.muted}
                    aria-label={p.muted ? `Unmute ${displayNumber(p.number)}` : `Mute ${displayNumber(p.number)}`}
                    title={p.muted ? 'Unmute' : 'Mute for everyone'}
                    disabled={busy === p.id}
                    onClick={() => void act(p.id, () => phone.mutePerson(p.id, !p.muted))}
                  >
                    {p.muted ? <MicOffIcon /> : <MicIcon />}
                  </button>
                )}
                <button
                  type="button"
                  className="icon-btn icon-btn-bad"
                  aria-label={`Remove ${displayNumber(p.number)} from the call`}
                  title="Remove from the call"
                  disabled={busy === p.id}
                  onClick={() => setConfirm(p.id)}
                >
                  <CloseIcon />
                </button>
              </div>
            )}
          </li>
        ))}
        <li className="person person-you">
          <div className="person-text">
            <span className="person-number">You</span>
            <span className="person-status">{youMuted ? 'Muted' : 'On the call'}</span>
          </div>
        </li>
      </ul>
      {others.length === 0 && <p className="setting-help">Only you are on the call. Add someone, or hang up.</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {canAdd && (
        <button type="button" className="btn btn-block people-add" onClick={onAdd}>
          Add someone
        </button>
      )}
    </dialog>
  );
}

/** Hanging up in a conference: leave (the others keep talking) or end it for everyone. */
export function LeaveSheet({ phone, onClose }: { phone: Phone; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog ref={ref} className="sheet sheet-small" aria-labelledby="leave-title" onClose={onClose} onCancel={onClose}>
      <h2 id="leave-title" className="leave-title">
        Hang up?
      </h2>
      <div className="leave-actions">
        <button
          type="button"
          className="btn btn-block"
          autoFocus
          disabled={busy}
          onClick={() => {
            phone.hangup();
            ref.current?.close();
          }}
        >
          Leave the call
          <span className="btn-sub">The others keep talking</span>
        </button>
        <button
          type="button"
          className="btn btn-block btn-danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await phone.endForEveryone();
            ref.current?.close();
          }}
        >
          End the call for everyone
        </button>
        <button type="button" className="btn btn-quiet btn-block" disabled={busy} onClick={() => ref.current?.close()}>
          Cancel
        </button>
      </div>
    </dialog>
  );
}
